import { ReactNode } from 'react';
import { JSX } from 'react';
import {
  ComponentRendering,
  constants,
  GetServerSideComponentProps,
  LayoutServiceData,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { BreadcrumbModel, N05BreadcrumbProps } from './N05Breadcrumb.types';
import { CreateBreadcrumbModel } from './N05Breadcrumb.service';
import { Breadcrumbs } from '@ahc-digital/component-library';
import { useCreateMarketingRenderingHame } from 'src/shared/useCreateMarketingRenderingName';
import { GetServerSidePropsContext } from 'next';
import { ImportedData, ImportedDataType } from 'lib/imported-data/ImportedData.types';
import { sparkleUtils } from 'src/shared/sparkle-utils';
import { ImportedDataProviderFactory } from 'lib/imported-data';
import { CreateDataRequest } from '@/lib/imported-data/ImportedDataRequest.types';

const getBreadcrumbText = (
  model: BreadcrumbModel,
  importedData?: ImportedData,
  sparkleType?: ImportedDataType
): string => {
  const currentPage = model.currentPage;
  const importedTitle = importedData?.navigationTitle;
  const pageTitle = currentPage?.navigationTitle?.value;
  const pageName = currentPage?.name;

  if (sparkleType !== undefined) {
    return importedTitle || pageTitle || pageName || '';
  }

  return pageTitle || pageName || '';
};

export const Default = (props?: N05BreadcrumbProps): JSX.Element => {
  const id = props?.params?.RenderingIdentifier;
  const showOnMobile = props?.rendering?.params?.ShowOnMobile;
  const model: BreadcrumbModel = CreateBreadcrumbModel(props?.fields?.data);
  const marketingRenderingName = useCreateMarketingRenderingHame(
    props?.rendering?.componentName as string
  );

  const breadcrumb = getBreadcrumbText(model, props?.importedData, props?.sparkleType);

  const links = (model?.ancestors?.map((ancestor, idx) => (
    <a key={idx} target="_self" href={ancestor?.url?.path}>
      {ancestor?.navigationTitle?.value || ancestor?.name || ''}
    </a>
  )) || []) as ReactNode[];

  return (
    <Breadcrumbs
      id={id || undefined}
      links={links}
      currentPage={breadcrumb}
      showOnMobile={Boolean(showOnMobile)}
      data-elastic-exclude=""
      data-component={marketingRenderingName}
    />
  );
};

export const getServerSideProps: GetServerSideComponentProps = async (
  _rendering: ComponentRendering,
  layoutData: LayoutServiceData,
  context: GetServerSidePropsContext
) => {
  const sparkleType = sparkleUtils.getSparklePageType(layoutData?.sitecore?.route?.templateId);

  if (process.env.JSS_MODE === constants.JSS_MODE.DISCONNECTED) {
    return { importedData: {} as ImportedData, sparkleType, layoutData };
  }

  const slug = sparkleUtils.getSlug(context.req.url ?? '');

  if (sparkleUtils.isEmptyOrWhiteSpace(slug)) return null;

  if (sparkleType === undefined) return null;

  const provider = ImportedDataProviderFactory.create();

  const request = CreateDataRequest(sparkleType, slug, context.req.headers.host ?? '');

  const result = await provider.getDataByRequest(request);

  return { importedData: result, sparkleType, layoutData };
};
